'use client'

import { usePrepareCart } from 'hooks/usePrepareCart'
import { usePagaleveEligibility } from '@cannect/hooks/usePagaleveEligibility'
import { canApplyPixDiscount } from '@cannect/utils/pixDiscount'
import { getPayableAmount } from '@cannect/utils/reshipping'
import { ArrowLeft, CreditCard, Receipt, Wallet } from 'lucide-react'
import { useEffect, useState } from 'react'
import OrderSummary from '../OrderSummary'
import Boleto from './Boleto'
import CreditCardComponent from './CreditCard'
import PaymentMethodSelector from './PaymentMethodSelector'
import { Pix } from './Pix'
import { Pagaleve } from './Pagaleve'
import * as S from './styles'

interface PaymentMethodProps {
  disabledPix?: boolean
  disabledCreditCard?: boolean
  disabledBoleto?: boolean
  finishOrderByPaymentMethod: any
  error: boolean
  isValidateSubmit?: boolean
  loadingContainer: boolean
  clearIntervalOutside: () => void
  hasMultipleMethods?: boolean
}

export function PaymentMethod({
  disabledPix,
  disabledCreditCard,
  disabledBoleto,
  finishOrderByPaymentMethod,
  error: _error,
  isValidateSubmit = false,
  loadingContainer,
  clearIntervalOutside,
  hasMultipleMethods
}: PaymentMethodProps) {
  const { lastOrderCreated } = usePrepareCart()
  // 010: com plano (Compra Recorrente) só existe cartão de crédito à vista: o
  // seletor é pulado (sem Pix, Boleto, Pagaleve nem combinações).
  const recurrencyPlan = Boolean(lastOrderCreated?.recurrencySummary)
  const [selectedMethod, setSelectedMethod] = useState<string | null>(recurrencyPlan ? 'credit_card' : null)
  const [showSelector, setShowSelector] = useState(!recurrencyPlan)
  const [isProcessingPayment, setIsProcessingPayment] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const payableAmount = getPayableAmount(lastOrderCreated)

  const hasOrderData = Boolean(lastOrderCreated?.person?.cpf && lastOrderCreated?.amount)
  const { isEligible: isPagaleveEligible, isChecking: isCheckingPagaleve } = usePagaleveEligibility({
    cpf: lastOrderCreated?.person?.cpf || '',
    amount: payableAmount,
    phone: lastOrderCreated?.person?.phone || '',
    email: lastOrderCreated?.person?.email || '',
    enabled: hasOrderData && !recurrencyPlan
  })

  useEffect(() => {
    if (recurrencyPlan) {
      setSelectedMethod('credit_card')
      setShowSelector(false)
    }
  }, [recurrencyPlan])

  useEffect(() => {
    return () => {
      clearIntervalOutside()
    }
  }, [clearIntervalOutside])

  const handleMethodSelect = (method: string) => {
    setSelectedMethod(method)
    setShowSelector(false)
  }

  const handleBackToSelector = () => {
    if (recurrencyPlan) return
    if (!isProcessingPayment && !isSubmitting) {
      setShowSelector(true)
      setSelectedMethod(null)
    }
  }

  const handleSubmittingChange = (submitting: boolean) => {
    setIsSubmitting(submitting)
  }

  const handleProcessingChange = (processing: boolean) => {
    setIsProcessingPayment(processing)
  }

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'credit_card':
      case 'credit_card_credit_card':
      case 'credit_card_boleto':
        return <CreditCard size={24} />
      case 'pix':
      case 'credit_card_pix':
      case 'pagaleve':
        return <Wallet size={24} />
      case 'boleto':
        return <Receipt size={24} />
      default:
        return null
    }
  }

  const getMethodTitle = (method: string) => {
    switch (method) {
      case 'credit_card':
        return 'Cartão de Crédito'
      case 'pix':
        return 'Pix'
      case 'pagaleve':
        return 'PIX Parcelado'
      case 'boleto':
        return 'Boleto Bancário'
      case 'credit_card_credit_card':
        return 'Dois Cartões de Crédito'
      case 'credit_card_pix':
        return 'Cartão de Crédito + Pix'
      case 'credit_card_boleto':
        return 'Cartão de Crédito + Boleto'
      default:
        return ''
    }
  }

  const renderPaymentComponent = () => {
    switch (selectedMethod) {
      case 'credit_card':
      case 'credit_card_credit_card':
      case 'credit_card_pix':
      case 'credit_card_boleto':
        return (
          <CreditCardComponent
            isValidateSubmit={isValidateSubmit}
            finishOrderByPaymentMethod={finishOrderByPaymentMethod}
            loadingContainer={loadingContainer}
            disabledPix={disabledPix}
            disabledCreditCard={disabledCreditCard}
            disabledBoleto={disabledBoleto}
            paymentMethod={
              selectedMethod as 'credit_card' | 'credit_card_credit_card' | 'credit_card_pix' | 'credit_card_boleto'
            }
            onSubmittingChange={handleSubmittingChange}
            onProcessingChange={handleProcessingChange}
          />
        )
      case 'pix':
        return <Pix loadingContainer={loadingContainer} onProcessingChange={handleProcessingChange} />
      case 'pagaleve':
        return <Pagaleve loadingContainer={loadingContainer} onProcessingChange={handleProcessingChange} />
      case 'boleto':
        return (
          <Boleto isValidateSubmit={isValidateSubmit} setError={() => {}} onProcessingChange={handleProcessingChange} />
        )
      default:
        return null
    }
  }

  return (
    <S.Container>
      <OrderSummary
        items={lastOrderCreated?.items}
        discountFreight={lastOrderCreated?.shippingDiscount}
        discountProduct={lastOrderCreated?.discount}
        freight={lastOrderCreated?.shipping}
        reshipping_value={lastOrderCreated?.reshipping_payer_value}
        subtotal={lastOrderCreated?.subtotal}
        amount={payableAmount}
        originType={lastOrderCreated?.origin}
        payments={lastOrderCreated?.charges}
        noImportCost={lastOrderCreated?.noImportCost}
        nationalFreight={lastOrderCreated?.national_freight}
      />
      {showSelector ? (
        <PaymentMethodSelector
          selectedMethod={selectedMethod}
          onSelectMethod={handleMethodSelect}
          totalAmount={payableAmount}
          disabledPix={disabledPix}
          disabledCreditCard={disabledCreditCard}
          disabledBoleto={disabledBoleto}
          hasMultipleMethods={hasMultipleMethods}
          applyPixDiscountOrder={canApplyPixDiscount(lastOrderCreated)}
          isPagaleveEligible={isPagaleveEligible}
          isCheckingPagaleve={isCheckingPagaleve}
        />
      ) : (
        <S.PaymentFormContainer>
          <S.PaymentFormHeader>
            <S.BackButton
              hidden={recurrencyPlan}
              onClick={handleBackToSelector}
              style={{ color: '#748351' }}
              disabled={isProcessingPayment || isSubmitting}>
              <ArrowLeft size={24} style={{ color: isProcessingPayment || isSubmitting ? '#ccc' : '#748351' }} />
              Formas de pagamento
            </S.BackButton>
            <S.SelectedMethodInfo>
              <span style={{ color: '#748351' }}>{getMethodIcon(selectedMethod || '')}</span>
              <span>{getMethodTitle(selectedMethod || '')}</span>
            </S.SelectedMethodInfo>
          </S.PaymentFormHeader>
          {renderPaymentComponent()}
        </S.PaymentFormContainer>
      )}
    </S.Container>
  )
}

export default PaymentMethod
